import { useEffect, useRef, useState } from 'react';
import type { PlaneFinderApp } from '../app/PlaneFinderApp';
import type { CaptureProgress, CaptureResult, ModelInfo, RegionStats, PlaneFinderStage } from '../core/types';

const ERROR_DISMISS_MS = 6000;

export interface PlaneFinderState {
  stage: PlaneFinderStage;
  model: ModelInfo | null;
  region: RegionStats | null;
  progress: CaptureProgress | null;
  result: CaptureResult | null;
  rejectedAt: number; // bumped on each failed detection (drives a transient hint)
  analysisReady: boolean; // worker precompute done (false → "preparing" hint, taps gated)
  error: string | null; // model-load / capture failure message (auto-dismisses)
}

/** Subscribe to the app's typed events and expose them as React state. */
export function usePlaneFinder(app: PlaneFinderApp | null): PlaneFinderState {
  const [stage, setStage] = useState<PlaneFinderStage>('empty');
  const [model, setModel] = useState<ModelInfo | null>(null);
  const [region, setRegion] = useState<RegionStats | null>(null);
  const [progress, setProgress] = useState<CaptureProgress | null>(null);
  const [result, setResult] = useState<CaptureResult | null>(null);
  const [rejectedAt, setRejectedAt] = useState(0);
  const [analysisReady, setAnalysisReady] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const errorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!app) return;
    // Seed from the app's current state — events emitted before this effect ran are
    // gone, and an already-running app (library embed) may be far past 'empty'.
    setStage(app.stage);
    setAnalysisReady(app.analysisReady);
    // Show a load/capture failure, then auto-dismiss (also cleared on success below).
    const showError = ({ message }: { message: string }) => {
      setError(message);
      if (errorTimer.current) clearTimeout(errorTimer.current);
      errorTimer.current = setTimeout(() => setError(null), ERROR_DISMISS_MS);
    };
    const unsubs = [
      app.events.on('stage', (s) => {
        setStage(s);
        if (s !== 'capturing') setProgress(null);
        if (s === 'view' || s === 'empty') setRegion(null);
      }),
      app.events.on('model-loaded', (m) => {
        setModel(m);
        setError(null);
      }),
      app.events.on('model-error', showError),
      app.events.on('analysis-ready', setAnalysisReady),
      app.events.on('region', setRegion),
      app.events.on('capture-progress', setProgress),
      app.events.on('capture-complete', (r) => {
        setResult(r);
        setError(null);
      }),
      app.events.on('capture-error', showError),
      app.events.on('detect-rejected', () => setRejectedAt((n) => n + 1)),
    ];
    return () => {
      unsubs.forEach((u) => u());
      if (errorTimer.current) clearTimeout(errorTimer.current);
    };
  }, [app]);

  return { stage, model, region, progress, result, rejectedAt, analysisReady, error };
}
